'use client'

import {useCallback, useEffect, useState} from 'react'
import {Edit2, Plus, Save, Trash2, X} from 'lucide-react'
import {apiGet, apiPost} from '@/lib/api'
import {PageHeader} from '@/components/PageHeader'
import {message} from '@/components/Toast'

type QuickReply = {
    id: string
    title?: string
    content: string
}

export function QuickReplyView() {
    const [replies, setReplies] = useState<QuickReply[]>([])
    const [loading, setLoading] = useState(true)
    const [editingId, setEditingId] = useState<string | null>(null)

    const fetchReplies = useCallback(async () => {
        setLoading(true)
        try {
            const res = await apiGet<{success: boolean; replies: QuickReply[]}>('/quick-replies', {silent: true})
            setReplies(res.replies || [])
        } catch { /* ignore */ }
        finally { setLoading(false) }
    }, [])

    useEffect(() => { fetchReplies() }, [fetchReplies])

    function addReply() {
        const id = `qr-${Date.now()}`
        setReplies([...replies, {id, title: '', content: ''}])
        setEditingId(id)
    }

    function updateReply(id: string, patch: Partial<QuickReply>) {
        setReplies(replies.map((r) => r.id === id ? {...r, ...patch} : r))
    }

    function removeReply(id: string) {
        setReplies(replies.filter((r) => r.id !== id))
        if (editingId === id) setEditingId(null)
    }

    async function handleSave() {
        const valid = replies.filter((r) => r.content.trim()).map((reply, index) => ({
            ...reply,
            title: reply.title?.trim() || `快捷回复 ${index + 1}`,
        }))
        try {
            await apiPost('/quick-replies', {replies: valid})
            setReplies(valid)
            message.success('快捷回复已保存')
        } catch { /* api.ts handles errors */ }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="快捷回复"
                action={
                    <button onClick={addReply} className="btn-primary h-9 px-4 text-sm">
                        <Plus size={16}/> 添加回复
                    </button>
                }
            />

            <div className="flex-1 overflow-y-auto p-6">
                <div className="mr-auto w-full max-w-4xl">
                    <div className="divide-y divide-hair overflow-hidden rounded-lg border border-hair bg-white">
                        {loading ? (
                            <div className="p-12 text-center text-sm text-muted">加载中...</div>
                        ) : replies.length === 0 ? (
                            <div className="p-12 text-center text-sm text-muted">暂无快捷回复</div>
                        ) : (
                            replies.map((reply, index) => {
                                const editing = editingId === reply.id
                                return (
                                    <div key={reply.id} className="flex items-start justify-between gap-4 p-5">
                                        <div className="min-w-0 flex-1">
                                            {editing ? (
                                                <input
                                                    type="text"
                                                    value={reply.title || ''}
                                                    onChange={(e) => updateReply(reply.id, {title: e.target.value})}
                                                    placeholder={`快捷回复 ${index + 1}`}
                                                    className="input h-10 text-sm font-medium"
                                                />
                                            ) : (
                                                <h4 className="text-sm font-medium text-ink">{reply.title || `快捷回复 ${index + 1}`}</h4>
                                            )}
                                            {editing ? (
                                                <textarea
                                                    value={reply.content}
                                                    onChange={(e) => updateReply(reply.id, {content: e.target.value})}
                                                    placeholder="输入快捷回复内容..."
                                                    rows={3}
                                                    className="input mt-3 min-h-24 resize-none text-sm leading-relaxed"
                                                    autoFocus
                                                />
                                            ) : (
                                                <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-muted">{reply.content || '未填写内容'}</p>
                                            )}
                                        </div>
                                        <div className="flex gap-2 shrink-0">
                                            <button
                                                onClick={() => setEditingId(editing ? null : reply.id)}
                                                className="icon-btn"
                                                title={editing ? '收起编辑' : '编辑'}
                                            >
                                                {editing ? <X size={16}/> : <Edit2 size={16}/>}
                                            </button>
                                            <button
                                                onClick={() => removeReply(reply.id)}
                                                className="icon-btn text-gray-400 hover:bg-red-50 hover:text-red-600"
                                                title="删除"
                                            >
                                                <Trash2 size={16}/>
                                            </button>
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div>

                    <div className="mt-5 flex justify-end">
                        <button onClick={handleSave} className="btn-primary h-9 px-5 text-sm">
                            <Save size={16}/> 保存修改
                        </button>
                    </div>
                </div>
            </div>
        </section>
    )
}
